import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import Slider from "src/components/slider";
import { fetchMessage, invalidateReshare, resendMessage } from "src/redux/actions/conversation";
import CollectionWidgetManager from "src/utils/collections.widget.service";
import { Contact, Conversation, Message, Recipient } from "src/types/conversations";
import DOMPurify from "dompurify";
import dateformat from "dateformat";

import Button from "src/components/button";
import Tooltip from "src/components/tooltip";
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import { PreviewLoader } from "../loaders";
import { RootState } from "src/redux/store";

import _ from "lodash";
import styles from "./resend.module.scss";
import cx from "classnames";

interface IResend {
  conversation: Conversation;
  show: boolean;
  onClose: () => void;
}

interface MailState {
  type: string;
  value: Contact;
  selected: boolean;
  label: string;
}

const Resend: React.FC<IResend> = ({ conversation, show, onClose }) => {
  interface InitialMessageType {
    item: Message;
    loaded: boolean;
    loading: boolean;
    resending?: boolean | null;
  }

  const dispatch = useDispatch();
  const initialMessageState: InitialMessageType = {
    item: {
      id: "",
      body: "",
      recipients: [],
      subject: "",
    },
    loaded: false,
    loading: false,
    resending: null,
  };
  const widgetManager = useRef(new CollectionWidgetManager());

  //selectors
  const { item, loaded, loading, resending, assets, sections } = useSelector((state: RootState) => {
    const conversationState =  _.get(
      state.conversations.conversation,
      `${conversation.id}`,
    )
    const message = _.get(conversationState,"message", initialMessageState)

    return {
      item : message.item,
      loaded: message.loaded,
      loading: message.loading,
      resending: message.resending,
      assets: conversationState.assets,
      sections: conversationState.sections
    }
  }

  );

  const [selectedTo, setSelectedTo] = React.useState<MailState[]>([]);
  const [selectedCc, setSelectedCc] = React.useState<MailState[]>([]);
  const [selectedBcc, setSelectedBcc] = React.useState<MailState[]>([]);

  useEffect(() => {
    if (show && !loaded) {
      dispatch(fetchMessage({ shareId: conversation.id! }));
    }
  }, [show]);

  useEffect(() => {
    if(resending === false && conversation.id) {
      dispatch(invalidateReshare({
        conversationId: conversation.id,
      }))
      onClose();
    }
  }, [resending])
  
  useEffect(() => {
    if (_.size(_.get(item, "recipients")) > 0 && show) {
      populateRecipients();
    }
  }, [item, show]);

  
  const renderMailBody = useCallback(() => {
    const html = widgetManager.current.getRawTemplate(_.get(item, "body"));
    const node = document.createElement("div");
    node.innerHTML = html;

    let assetsWidgets = node.querySelectorAll(".assetsGrid");

    if (conversation.sections && conversation.sections.length > 0) {
      assetsWidgets = node.querySelectorAll(".sectionCard");
    }

    for (let i = 0; i < assetsWidgets.length; i++) {
      const assetWidget = assetsWidgets[i];

      const imgNodes: HTMLImageElement[] = Array.from(
        assetWidget.querySelectorAll(".mceNonEditable img"),
      ) as HTMLImageElement[];
      updateImage(imgNodes, conversation);
    }

    const anchorTag = node.querySelectorAll(".mceNonEditable a");
    if (anchorTag && anchorTag.length > 0) {
      for (let i = 0; i < anchorTag.length; i++) {
        const element = anchorTag[i] as HTMLAnchorElement;

        element.href = "";
        element.setAttribute(
          "style",
          "pointer-events: none; font-weight: 600; font-size: 0.875em; color: #353535; font-family: 'Gilroy', Helvetica, Arial, sans-serif; text-decoration: none;",
        );
      }
    }

    return (
      <div
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(node.innerHTML) }}
      ></div>
    );
  }, [item]);

  const updateImage = (imgNodes: HTMLImageElement[], share: any) => {
    const DefaultIcon = "/images/defaultIcons/default_asset.jpg"
    if (imgNodes && imgNodes.length > 0) {
      if (assets && assets.length > 0) {
        for (let i = 0; i < imgNodes.length; i++) {
          const element = imgNodes[i];

          const asset = assets[i];

          if (asset) {
            const image = asset.icon ? asset.icon.thumbnail : DefaultIcon;
            element.src = image || "";
          }
        }
      } else if (sections) {
        const element = imgNodes[0];

        const image = share.icon ? share.icon.thumbnail : DefaultIcon;
        element.src = image;
      }
    }
  };

  const populateRecipients = useCallback(() => {    
    const recipients = _.get(item, "recipients", []);
    const to = recipients
      .filter((recipient: Recipient) => recipient.type === "TO")
      .map((recipient: Recipient) => {
        return {
          label:
            recipient.contact.firstName +
            " " +
            (recipient.contact.lastName || ""),
          type: "contact",
          value: recipient.contact,
          selected: true,
        };
      });
    const cc = recipients
      .filter((recipient: Recipient) => recipient.type === "CC")
      .map((recipient: Recipient) => {
        return {
          label:
            recipient.contact.firstName +
            " " +
            (recipient.contact.lastName || ""),
          type: "contact",
          value: recipient.contact,
          selected: false,
        };
      });
    const bcc = recipients
      .filter((recipient: Recipient) => recipient.type === "BCC")
      .map((recipient: Recipient) => {
        return {
          label:
            recipient.contact.firstName +
            " " +
            (recipient.contact.lastName || ""),
          type: "contact",
          value: recipient.contact,
          selected: false,
        };
      });

    setSelectedCc(cc);
    setSelectedBcc(bcc);
    setSelectedTo(to);
  }, [item]);

  const handleResend = () => {
    const selectedMailIds = selectedTo
      .filter((recipient) => recipient.selected)
      .map((recipient) => recipient);
    const isDNDRecipient = selectedMailIds.some(
      (recipient) => recipient.value.doNotDisturb,
    );
    if (selectedMailIds.length === 0) {
      toast(
        <Toast.Failure
          message={
            "We don’t see a recipient in the ‘To’ field. Add an email id."
          }
        />,
      );
    } else if (isDNDRecipient) {
      toast(
        <Toast.Failure
          message={
            "One or more recipients have enabled DND and will not receive your email."
          }
        />,
      );
    } else {
      const request = {
        conversationId: conversation.id!,
        messageId: _.get(item, "id",""),
        toAddresses: selectedMailIds.map((recipient) => {
          return {
            type: "email",
            value: recipient.value.email,
            selected: recipient.selected,
          }
        })
      }
      dispatch(resendMessage(request));
    }
  };

  return (
    <Slider.Dialog show={show} onClose={onClose} width="650px">
      <Slider.Header className={styles.header}>
        <div className={styles.titleContainer}>
          <p className={styles.title}>resend conversation</p>
          <p className={styles.subtitle}>
            {dateformat(conversation.createdDate, "dd mmmm yyyy, hh:mm tt")}
          </p>
        </div>

        <i className="paperflite-close" onClick={onClose}></i>
      </Slider.Header>
      <Slider.Body className={styles.body}>
        {loading && <PreviewLoader />}
        {loaded && (
          <section className={styles.email}>
            <div className={styles.recipients}>
              <div className={cx(styles.to, styles.mails)}>
                <label>To</label>
                <div className={styles.tags}>
                  {selectedTo.map((recipient) => (
                    <Tag
                      recipient={recipient}
                      key={recipient.label}
                      editable={true}
                      onClick={(recipient) => {
                        setSelectedTo(
                          selectedTo.map((item) => {
                            if (item.label === recipient.label) {
                              return {
                                ...item,
                                selected: !item.selected,
                              };
                            }
                            return item;
                          }),
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
              {selectedCc.length > 0 && (
                <div className={cx(styles.cc, styles.mails)}>
                  <label>CC</label>
                  <div className={styles.tags}>
                    {selectedCc.map((recipient) => (
                      <Tag recipient={recipient} key={recipient.label} />
                    ))}
                  </div>
                </div>
              )}
              {selectedBcc.length > 0 && (
                <div className={cx(styles.bcc, styles.mails)}>
                  <label>BCC</label>
                  <div className={styles.tags}>
                    {selectedBcc.map((recipient) => (
                      <Tag recipient={recipient} key={recipient.label} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.subject}>
              <div className={styles.input}>
                <label>Subject</label>
                <span className={styles.value}>{_.get(item, "subject", "")}</span>
              </div>
            </div>
            <div className={styles.content}>{renderMailBody()}</div>
          </section>
        )}
      </Slider.Body>
      <Slider.Actions>
        <Button
          text={resending ? "sending" : "send"}
          icon="paperflite-send"
          variant="primary"
          disabled={resending || false}
          onClick={handleResend}
        />
        <Button text="cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

export default Resend;

interface ITag {
  recipient: MailState;
  editable?: boolean;
  // eslint-disable-next-line no-unused-vars
  onClick?: (recipient: MailState) => void;
}
const Tag: React.FC<ITag> = ({ recipient, editable = false, onClick }) => {
  return (
    <div
      className={cx(styles.tag, {
        [styles.unSelected]: !recipient.selected && editable,
        [styles.dndTag]: recipient.value.doNotDisturb,
      })}
      onClick={() => onClick && onClick(recipient)}
    >
      <Tooltip
        placement="top"
        content={
          recipient.value.doNotDisturb ? "This contact has enabled DND" : ""
        }
      >
        <span className={styles.label}>{recipient.label}</span>
      </Tooltip>
      {editable && (
        <i
          className={cx({
            ["paperflite-radio-button-unselected"]: !recipient.selected,
            ["fa-solid fa-circle-check"]: recipient.selected,
          })}
        />
      )}
    </div>
  );
};
